import { useLocalStorage } from '@rehooks/local-storage';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RELEASE_PROMPTS } from './ReleasePromptContent';
import { ReleasePromptDialog } from './ReleasePromptDialog';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { mobileConfig } from '../../commons/config';
import {
  PromptState,
  PromptStates,
  PromptStatus,
  RELEASE_PROMPTS_LOCAL_STORAGE_KEY,
  ReleasePromptData,
} from '../types';

const releasePromptDeferredSeconds = 60 * 60 * 24;

export const ReleasePrompt = () => {
  const analytics = useAnalytics();
  const navigate = useNavigate();

  const [businessPlanPromptStatus] = useLocalStorage<PromptState | null>(`ReleasePrompt/1`, null);
  const [promptStates, setPromptStates] = useLocalStorage<PromptStates | null>(
    RELEASE_PROMPTS_LOCAL_STORAGE_KEY,
    {
      states: businessPlanPromptStatus ? { 1: businessPlanPromptStatus } : {},
    }
  );

  const now = new Date();
  const { isMobile } = mobileConfig();
  const [promptToShow] = useState<ReleasePromptData | null>(() => {
    if (promptStates?.promptsDisabled) return null;
    const enabledPrompts = RELEASE_PROMPTS.filter(
      (releasePrompt) =>
        releasePrompt.enabled &&
        (!releasePrompt.expiration || new Date() < releasePrompt.expiration) &&
        (!isMobile || !releasePrompt.skipMobile)
    ).sort((a, b) => b.promptId - a.promptId);

    return (
      enabledPrompts.find((prompt) => !promptStates || !promptStates.states[prompt.promptId]) ??
      enabledPrompts.find((prompt) => {
        const promptStatus = promptStates?.states[prompt.promptId];
        if (promptStatus?.status === 'deferred') {
          const deferredAt = new Date(promptStatus.timestamp);
          const secondsSinceDeferred = (now.getTime() - deferredAt.getTime()) / 1000;
          return secondsSinceDeferred >= releasePromptDeferredSeconds;
        }
        return false;
      }) ??
      null
    );
  });

  const setPromptStatus = useCallback(
    (prompt: ReleasePromptData, status: PromptStatus) => {
      analyticsTrack(analytics, AnalyticsEvent.RELEASE_PROMPT_CLOSED, {
        promptStatus: status,
        promptCode: prompt.code,
      });
      setPromptStates({
        states: {
          ...promptStates?.states,
          ...{
            [prompt.promptId]: {
              status,
              timestamp: new Date().toISOString(),
            },
          },
        },
      });
    },
    [analytics, promptStates?.states, setPromptStates]
  );

  const [closed, setClosed] = useState(false);
  const onNavigate = useCallback(() => {
    setClosed(true);
    if (promptToShow) {
      setPromptStatus(promptToShow, 'navigated');
      if (promptToShow?.route) navigate(promptToShow?.route);
      else if (promptToShow?.action) promptToShow.action();
    }
  }, [promptToShow, navigate, setPromptStatus]);

  const onCheckLater = useCallback(() => {
    setClosed(true);
    if (promptToShow) setPromptStatus(promptToShow, 'deferred');
  }, [promptToShow, setPromptStatus]);

  const onCancel = useCallback(() => {
    setClosed(true);
    if (promptToShow) setPromptStatus(promptToShow, 'skipped');
  }, [promptToShow, setPromptStatus]);

  return !closed && !!promptToShow?.promptId ? (
    <ReleasePromptDialog
      prompt={promptToShow}
      onNavigate={onNavigate}
      onCheckLater={onCheckLater}
      onCancel={onCancel}
    />
  ) : null;
};
